/**
 * SeasonView - Read-only view of a season showing all rounds, submissions, scores
 * Route: /:leagueSlug/seasons/:seasonId
 */

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import RoundCard from '@/components/RoundCard';
import { useStore } from '@/lib/store';
import { useEffect, useState, useMemo } from 'react';
import * as api from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { getLeagueNameFromSlug, isValidSlug } from '@/lib/league-slugs';
import type { League, Round, Season } from '@/lib/mock-data';

const SeasonView = () => {
  const { leagueSlug, seasonId } = useParams<{ leagueSlug: string; seasonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useStore();
  
  const [season, setSeason] = useState<Season | null>(null);
  const [league, setLeague] = useState<League | null>(null);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Validate slug
  const leagueName = leagueSlug && isValidSlug(leagueSlug) 
    ? getLeagueNameFromSlug(leagueSlug) 
    : null;

  useEffect(() => {
    const loadSeasonData = async () => {
      if (!seasonId || !leagueName) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Find league
        const allLeagues = await api.leagueAPI.getAllLeagues();
        const foundLeague = allLeagues.find(l => 
          l.name.toLowerCase() === leagueName.toLowerCase()
        );
        
        if (!foundLeague) {
          console.error(`League "${leagueName}" not found`);
          setIsLoading(false);
          return;
        }
        
        setLeague(foundLeague);

        // Fetch season
        const { data: seasonData, error: seasonError } = await supabase
          .from('seasons')
          .select('*')
          .eq('id', seasonId)
          .eq('league_id', foundLeague.id)
          .single();

        if (seasonError || !seasonData) {
          console.error('Error fetching season:', seasonError);
          setIsLoading(false);
          return;
        }

        const seasonObj: Season = {
          id: seasonData.id,
          league_id: seasonData.league_id,
          name: seasonData.name,
          description: seasonData.description || undefined,
          invite_code: seasonData.invite_code,
          created_by: seasonData.created_by || '',
          max_players: seasonData.max_players,
          points_per_round: seasonData.points_per_round,
          scoring: seasonData.scoring as any,
          require_spotify: seasonData.require_spotify,
          status: seasonData.status,
          sort_index: seasonData.sort_index || undefined,
          created_at: seasonData.created_at,
        };
        
        setSeason(seasonObj);

        // Fetch rounds for this season
        // Rounds are linked to seasons via season_id or by matching season name
        const { data: roundsData, error: roundsError } = await supabase
          .from('rounds')
          .select('*')
          .eq('league_id', foundLeague.id)
          .order('round_number', { ascending: true });

        if (roundsError) {
          console.error('Error fetching rounds:', roundsError);
          setRounds([]);
        } else {
          // Filter rounds by season name (since rounds have season field)
          const seasonRounds = (roundsData || [])
            .filter((r: any) => {
              // Match by season name or season_id if available
              return r.season === seasonObj.name || 
                     (r.season_id && r.season_id === seasonId) ||
                     r.season?.includes(seasonObj.name);
            })
            .map((dbRound: any) => ({
              id: dbRound.id,
              league_id: dbRound.league_id,
              round_number: dbRound.round_number,
              theme: dbRound.theme,
              description: dbRound.description || undefined,
              season: dbRound.season || undefined,
              submission_deadline: dbRound.submission_deadline,
              voting_deadline: dbRound.voting_deadline,
              status: dbRound.status,
              created_at: dbRound.created_at,
            }));
          
          setRounds(seasonRounds);
          console.log(`[SeasonView] Loaded ${seasonRounds.length} rounds for season "${seasonObj.name}"`);
        }
      } catch (error) {
        console.error('Error loading season data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSeasonData();
  }, [seasonId, leagueName]);

  // Build back URL
  const backUrl = leagueSlug ? `/${leagueSlug}/leagues` : '/leagues';

  if (!leagueSlug || !isValidSlug(leagueSlug)) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">Invalid league URL</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading season...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!season || !league) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Season Not Found</h3>
              <p className="text-muted-foreground mb-4">
                The season could not be found.
              </p>
              <Button variant="outline" onClick={() => navigate(backUrl)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Leagues
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(backUrl)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Leagues
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {season.name}
              </h1>
              {season.description && (
                <p className="text-muted-foreground mb-4">{season.description}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <Badge variant="secondary">
                  {rounds.length} round{rounds.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Rounds */}
        <div className="space-y-6">
          {rounds.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Rounds</h3>
                <p className="text-muted-foreground">
                  This season doesn't have any rounds yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            rounds.map((round) => (
              <RoundCard
                key={round.id}
                round={round}
                leagueId={league.id}
                league={league}
                mode="archive"
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default SeasonView;
